import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button, Card, Textarea, useToast } from "../ui";

const DIREZIONE = { INVIATA: 100000000, RICEVUTA: 100000001 };

// Estrae solo il testo dall'HTML dell'email: un DOMParser non e' mai
// inserito nel documento live, quindi script/onerror/onload nel corpo
// (specie nelle email RICEVUTE, provenienti da una mailbox esterna) restano
// inerti. Evita di introdurre dangerouslySetInnerHTML su contenuto non fidato.
function htmlToText(html) {
  if (!html) return "";
  try {
    // Converte i tag di blocco in a-capo prima di leggere il testo: senza
    // questo, textContent incolla i paragrafi senza spazi/newline, rendendo
    // inutili i pattern di taglio della citazione (vedi stripQuotedReply)
    const withBreaks = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|tr|table|li|blockquote|h[1-6])>/gi, "\n");
    const doc = new DOMParser().parseFromString(withBreaks, "text/html");
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  } catch {
    return html;
  }
}

// Taglia la citazione del messaggio precedente che i client email (Gmail,
// Outlook, ecc.) inseriscono nel corpo della risposta ("Il giorno ... ha
// scritto:", intestazioni "Da:/Inviato:", "-----Messaggio originale-----",
// righe quotate "> "). Riconoscimento euristico sul testo: non c'e' un
// marcatore HTML affidabile su cui contare per ogni client.
const QUOTE_PATTERNS = [
  /\n\s*(il giorno|on)\s[\s\S]{0,150}?(ha scritto|wrote):\s*[\s\S]*$/i,
  /\n\s*(da|from):\s*.+\n\s*(inviato|sent):\s*.+[\s\S]*$/i,
  /\n\s*-{2,}\s*(messaggio originale|original message)\s*-{2,}[\s\S]*$/i,
  /\n(>.*\n?)+$/,
];

function stripQuotedReply(text) {
  let cutIndex = text.length;
  for (const pattern of QUOTE_PATTERNS) {
    const match = text.match(pattern);
    if (match && typeof match.index === "number" && match.index < cutIndex) {
      cutIndex = match.index;
    }
  }
  return text.slice(0, cutIndex).trim();
}

function ConversazioneEmail({ richiestaId }) {
  const { apiRequest, getAccessToken } = useAuth();
  const { showToast } = useToast();

  const [messaggi, setMessaggi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [corpo, setCorpo] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const [notaDrafts, setNotaDrafts] = useState({});
  const [notaOpenId, setNotaOpenId] = useState(null);
  const [savingNotaId, setSavingNotaId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(
        `/richieste-intervento/${richiestaId}/conversazione`,
      );
      setMessaggi(res?.messaggi || []);
    } catch (err) {
      showToast(
        err.message || "Errore nel caricamento della conversazione",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [apiRequest, richiestaId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadAllegato = async (fileId, fileName) => {
    try {
      const token = await getAccessToken();
      const res = await fetch(
        `/api/richieste-intervento/${richiestaId}/conversazione/allegati/${fileId}/download`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error(`Errore download: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err.message || "Errore nel download dell'allegato", "error");
    }
  };

  const invia = async () => {
    if (!corpo.trim()) {
      showToast("Scrivi un messaggio prima di inviare", "error");
      return;
    }
    setSending(true);
    try {
      const payload = new FormData();
      payload.append("corpo", corpo);
      files.forEach((f) => payload.append("allegati", f));
      await apiRequest(
        `/richieste-intervento/${richiestaId}/conversazione/reply`,
        { method: "POST", body: payload },
      );
      showToast("Risposta inviata", "success");
      setCorpo("");
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      showToast(err.message || "Errore nell'invio della risposta", "error");
    } finally {
      setSending(false);
    }
  };

  const salvaNota = async (messaggioId) => {
    const testo = (notaDrafts[messaggioId] || "").trim();
    if (!testo) {
      showToast("Scrivi una nota prima di salvare", "error");
      return;
    }
    setSavingNotaId(messaggioId);
    try {
      const res = await apiRequest(
        `/richieste-intervento/${richiestaId}/conversazione/${messaggioId}/note`,
        { method: "POST", body: JSON.stringify({ testo }) },
      );
      setMessaggi((prev) =>
        prev.map((m) =>
          m.id === messaggioId
            ? { ...m, note: [...(m.note || []), res.nota] }
            : m,
        ),
      );
      setNotaDrafts((d) => ({ ...d, [messaggioId]: "" }));
      setNotaOpenId(null);
      showToast("Nota aggiunta", "success");
    } catch (err) {
      showToast(err.message || "Errore nel salvataggio della nota", "error");
    } finally {
      setSavingNotaId(null);
    }
  };

  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("it-IT") : "—");

  return (
    <Card className="mb-4">
      <h2 className="font-semibold text-gray-900 mb-3">Conversazione email</h2>

      {loading ? (
        <div className="text-sm text-gray-500">Caricamento…</div>
      ) : messaggi.length === 0 ? (
        <div className="text-sm text-gray-500 mb-4">
          Nessuna email scambiata per questa richiesta.
        </div>
      ) : (
        <div className="space-y-3 mb-4 max-h-[28rem] overflow-y-auto pr-1">
          {messaggi.map((m) => {
            const inviata = m.direzione === DIREZIONE.INVIATA;
            return (
              <div key={m.id}>
                <div
                  className={`flex ${inviata ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg p-3 text-sm ${
                      inviata
                        ? "bg-primary-50 border border-primary-100"
                        : "bg-gray-50 border border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-1">
                      <span className="font-medium truncate">
                        {inviata ? `A: ${m.destinatario}` : `Da: ${m.mittente}`}
                      </span>
                      <span className="flex-shrink-0">
                        {fmtDateTime(m.data)}
                      </span>
                    </div>
                    {m.oggetto && (
                      <div className="text-xs text-gray-400 mb-1 truncate">
                        {m.oggetto}
                      </div>
                    )}
                    {inviata && (
                      <div className="text-xs text-gray-500 italic mb-1">
                        Inviata da:{" "}
                        {m.inviatoDaNome || "Sistema (approvazione)"}
                      </div>
                    )}
                    <div className="text-gray-800 whitespace-pre-wrap">
                      {stripQuotedReply(htmlToText(m.corpo))}
                    </div>
                    {m.allegati?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.allegati.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => downloadAllegato(a.id, a.name)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                            title={a.name}
                          >
                            📎 {a.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={`flex mt-1 ${inviata ? "justify-end" : "justify-start"}`}
                >
                  <div className="max-w-[85%] w-full space-y-1">
                    {m.note?.map((n) => (
                      <div
                        key={n.id}
                        className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-900"
                      >
                        <span className="font-medium">{n.utenteNome}</span>
                        <span className="text-amber-600">
                          {" "}
                          · {fmtDateTime(n.data)}
                        </span>
                        <div className="whitespace-pre-wrap">{n.testo}</div>
                      </div>
                    ))}
                    {notaOpenId === m.id ? (
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={notaDrafts[m.id] || ""}
                          onChange={(e) =>
                            setNotaDrafts((d) => ({
                              ...d,
                              [m.id]: e.target.value,
                            }))
                          }
                          placeholder="Nota interna (non visibile al fornitore)…"
                          className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                          disabled={savingNotaId === m.id}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={() => salvaNota(m.id)}
                          loading={savingNotaId === m.id}
                        >
                          Salva
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setNotaOpenId(null)}
                          disabled={savingNotaId === m.id}
                        >
                          Annulla
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setNotaOpenId(m.id)}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        + Nota interna
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-gray-100 pt-3 space-y-2">
        <Textarea
          value={corpo}
          onChange={(e) => setCorpo(e.target.value)}
          rows={3}
          placeholder="Scrivi una risposta al fornitore…"
          disabled={sending}
        />
        <div className="flex items-center justify-between gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            disabled={sending}
            className="text-xs text-gray-500"
          />
          <Button
            onClick={invia}
            loading={sending}
            disabled={sending || !corpo.trim()}
          >
            Invia risposta
          </Button>
        </div>
        {files.length > 0 && (
          <div className="text-xs text-gray-500">
            {files.length} allegat{files.length === 1 ? "o" : "i"} selezionat
            {files.length === 1 ? "o" : "i"}
          </div>
        )}
      </div>
    </Card>
  );
}

export default ConversazioneEmail;
